import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { useClient } from '../../../core/hooks/useClient';
import { useLinkedClientAccount } from '../../../core/hooks/useLinkedClientAccount';
import { resolveProjectContact } from '../../../core/utils/projectContact';
import { useDocuments } from '../../../core/hooks/useDocuments';
import type { Document } from '../../../core/models/Document';
import { DocumentStatus, DocumentSignatureStatus } from '../../../core/models/Document';
import { ClientStatus } from '../../../core/models/Client';
import { format } from 'date-fns';
import { CheckCircle, Clock, XCircle, Building2, Mail, Phone, MapPin, ExternalLink, FileText, ArrowLeft, Trash2, Download, Share2, Link } from 'lucide-react';
import { PDFPreview } from './PDFPreview';
import type { Client } from '../../../core/models/Client';

interface DocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document;
  onRequestSignatures?: () => void;
  onSign?: () => void;
  onBackToDocuments?: () => void;
  onCopySigningLink?: () => void;
  onShareLink?: () => void;
  onDelete?: () => void;
  copyingLink?: boolean;
  sharingLink?: boolean;
  client?: Client | null;
}

// Helper function to safely convert various date formats to Date object
const safeDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;

  // If it's already a Date object
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // If it's a Firestore Timestamp (has toDate method)
  if (dateValue && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }

  // If it's a number (timestamp)
  if (typeof dateValue === 'number') {
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  }

  // If it's a string, try to parse it
  if (typeof dateValue === 'string') {
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

export const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  onClose,
  document,
  onRequestSignatures,
  onSign,
  onBackToDocuments,
  onCopySigningLink,
  onShareLink,
  onDelete,
  copyingLink = false,
  sharingLink = false,
  client: clientProp,
}) => {
  const { client: clientFromHook, loading: clientLoading, error: clientError } = useClient(document.clientId);
  const client = clientProp || clientFromHook;
  const linkedClientId = client?.clientId || client?.organizationId || null;
  const { clientAccount } = useLinkedClientAccount(linkedClientId);
  const contact = resolveProjectContact(client, clientAccount);
  const { generateDocumentPdf } = useDocuments();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.COMPLETED:
        return 'success';
      case DocumentStatus.PENDING_SIGNATURES:
        return 'warning';
      case DocumentStatus.CANCELLED:
      case DocumentStatus.EXPIRED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getClientStatusColor = (status: ClientStatus) => {
    switch (status) {
      case ClientStatus.ACTIVE:
        return 'success';
      case ClientStatus.INACTIVE:
        return 'error';
      case ClientStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSignatureStatusIcon = (status: DocumentSignatureStatus) => {
    switch (status) {
      case DocumentSignatureStatus.SIGNED:
        return <CheckCircle className="h-4 w-4 text-success" />;
      case DocumentSignatureStatus.PENDING:
        return <Clock className="h-4 w-4 text-warning" />;
      case DocumentSignatureStatus.REJECTED:
        return <XCircle className="h-4 w-4 text-error" />;
      default:
        return null;
    }
  };

  const pendingSignatures = document.signatures.filter(
    (sig) => sig.status === DocumentSignatureStatus.PENDING
  );
  const signedCount = document.signatures.filter(
    (sig) => sig.status === DocumentSignatureStatus.SIGNED
  ).length;

  // Check if there are any pending signatures (either client or organization)
  const hasPendingSignatures = pendingSignatures.length > 0;

  const handleDownloadPdf = async () => {
    if (!document.content) {
      alert('Document has no content to download.');
      return;
    }

    setDownloadingPdf(true);
    try {
      const result = await generateDocumentPdf(document.id);
      if (result.pdfUrl) {
        // Create a temporary link to download the PDF
        const link = window.document.createElement('a');
        link.href = result.pdfUrl;
        link.download = `${document.title || 'document'}.pdf`;
        link.target = '_blank';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      }
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      alert(error.message || 'Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{document.title}</span>
            <Badge variant={getStatusColor(document.status)}>
              {document.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Project Connection Details */}
          {client && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-gray-500" />
                    <CardTitle className="text-lg">Project Connection</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getClientStatusColor(client.status)}>
                      {client.status}
                    </Badge>
                  </div>
                </div>
                <CardDescription>
                  Document is connected to this project
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Project Name</p>
                      <p className="font-medium">{client.name}</p>
                    </div>
                  </div>
                  {contact.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Client Email</p>
                        <a
                          href={`mailto:${contact.email}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {contact.email}
                        </a>
                      </div>
                    </div>
                  )}
                  {contact.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Client Phone</p>
                        <a
                          href={`tel:${contact.phone}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {contact.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {contact.address && (
                    <div className="flex items-start gap-3 md:col-span-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Client Address</p>
                        <p className="font-medium">
                          {contact.address.street}<br />
                          {contact.address.city}, {contact.address.state} {contact.address.postalCode}
                          {contact.address.country && <>, {contact.address.country}</>}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/clients/${client.id}`, '_blank')}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Project Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {clientLoading && (
            <Card>
              <CardContent className="py-4">
                <p className="text-sm text-gray-500">Loading project details...</p>
              </CardContent>
            </Card>
          )}

          {!clientLoading && !client && document.clientId && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gray-500" />
                  <CardTitle className="text-lg">Project Connection</CardTitle>
                </div>
                <CardDescription>Project connection information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  {clientError ? (
                    <p className="text-error">Error loading project: {clientError.message}</p>
                  ) : (
                    <p>Project ID: {document.clientId} (Project not found)</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {!document.clientId && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gray-500" />
                  <CardTitle className="text-lg">Project Connection</CardTitle>
                </div>
                <CardDescription>Project connection information</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">No project associated with this document</p>
              </CardContent>
            </Card>
          )}

          {/* Document Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Document Details</CardTitle>
              <CardDescription>Document metadata and information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div>
                  <p className="text-gray-600">Template Type</p>
                  <p className="font-medium">{document.templateType?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Version</p>
                  <p className="font-medium">{document.version}</p>
                </div>
                <div>
                  <p className="text-gray-600">Created</p>
                  <p className="font-medium">
                    {(() => {
                      const createdDate = safeDate(document.createdAt);
                      return createdDate ? format(createdDate, 'PPp') : 'N/A';
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Last Updated</p>
                  <p className="font-medium">
                    {(() => {
                      const updatedDate = safeDate(document.updatedAt);
                      return updatedDate ? format(updatedDate, 'PPp') : 'N/A';
                    })()}
                  </p>
                </div>
                {document.description && (
                  <div className="md:col-span-2">
                    <p className="text-gray-600">Description</p>
                    <p className="font-medium">{document.description}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Signatures Section */}
          {document.signatures.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Signatures ({signedCount}/{document.signatures.length})
                </CardTitle>
                <CardDescription>Document signature status and history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {document.signatures.map((signature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        {getSignatureStatusIcon(signature.status)}
                        <div>
                          <p className="font-medium">{signature.name}</p>
                          {signature.title && (
                            <p className="text-sm text-gray-600">{signature.title}</p>
                          )}
                          {signature.email && (
                            <p className="text-xs text-gray-500">{signature.email}</p>
                          )}
                          {signature.signedAt && (() => {
                            const signedDate = safeDate(signature.signedAt);
                            return signedDate ? (
                              <p className="text-xs text-gray-500">
                                Signed: {format(signedDate, 'PPp')}
                              </p>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <Badge
                        variant={
                          signature.status === DocumentSignatureStatus.SIGNED
                            ? 'success'
                            : signature.status === DocumentSignatureStatus.REJECTED
                            ? 'error'
                            : 'warning'
                        }
                      >
                        {signature.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signed PDF Preview */}
          {document.signedPdfUrl && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Signed Document
                </CardTitle>
                <CardDescription>View the signed PDF document</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <PDFPreview 
                    key={document.signedPdfUrl} 
                    file={document.signedPdfUrl} 
                    showDownload={true} 
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Document Content (show only if no signed PDF) */}
          {!document.signedPdfUrl && document.content && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Document Content</CardTitle>
                    <CardDescription>Generated document content from template</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {downloadingPdf ? 'Generating PDF...' : 'Download PDF'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <iframe
                  srcDoc={document.content || ''}
                  className="w-full rounded-lg border bg-white"
                  style={{ height: '600px' }}
                  title="Document Preview"
                  sandbox="allow-same-origin"
                />
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={onDelete}
                title="Delete document"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {onCopySigningLink && (
              <Button
                variant="outline"
                onClick={onCopySigningLink}
                disabled={copyingLink || !contact.email}
              >
                <Link className="mr-2 h-4 w-4" />
                {copyingLink ? 'Creating...' : 'Create Signing Link'}
              </Button>
            )}
            {onShareLink && (
              <Button
                variant="outline"
                onClick={onShareLink}
                disabled={sharingLink}
              >
                <Share2 className="mr-2 h-4 w-4" />
                {sharingLink ? 'Sharing...' : 'Share Link'}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onBackToDocuments && (
              <Button variant="outline" onClick={onBackToDocuments}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            {onRequestSignatures &&
              document.status === DocumentStatus.DRAFT &&
              pendingSignatures.length === 0 && (
                <Button onClick={onRequestSignatures}>
                  Request Signatures
                </Button>
              )}
            {onSign &&
              hasPendingSignatures &&
              (document.status === DocumentStatus.PENDING_SIGNATURES || document.status === DocumentStatus.DRAFT) && (
                <Button onClick={onSign}>
                  Sign
                </Button>
              )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
